import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// Pages
import Login from './pages/Login';
import Manutencao from './pages/Manutencao';
import Dashboard from './pages/Dashboard';
import Projetos from './pages/Projetos';
import ClientForm from './pages/ClientForm';
import ClientList from './pages/ClientList';
import Propostas from './pages/Propostas';
import Atividades from './pages/Atividades';
import NewProject from './pages/NewProject';
import ArchivedProjects from './pages/ArchivedProjects';
import Reports from './pages/Reports';
import Suporte from './pages/Suporte';
import BaseConhecimento from './pages/BaseConhecimento';
import Partners from './pages/admin/Partners';
import UsersPage from './pages/admin/Users';
import Etapas from './pages/admin/Etapas';
import Categorias from './pages/admin/Categorias';
import Marcas from './pages/admin/Marcas';
import ConfigPage from './pages/admin/Config';
import Configuracoes from './pages/admin/Configuracoes';
import ConfiguracaoEmpresa from './pages/admin/ConfiguracaoEmpresa';
import Logs from './pages/admin/Logs';
import EliminarDados from './pages/admin/EliminarDados';
import Cargos from './pages/admin/Cargos';
import StatusProjeto from './pages/admin/StatusProjeto';
import TiposProjeto from './pages/admin/TiposProjeto';
import AcessoBloqueado from './pages/AcessoBloqueado';
// MinhaAssinatura removido - agora está no Admin Panel separado

// Payment Pages
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCanceled from './pages/PaymentCanceled';

// Admin Pages (hidden)
import TenantsAdmin from './pages/admin/TenantsAdmin';

// Financeiro Module (Add-on)
import FinanceiroDashboard from './pages/financeiro/FinanceiroDashboard';
import FinanceiroServicos from './pages/financeiro/FinanceiroServicos';
import FinanceiroFinalizados from './pages/financeiro/FinanceiroFinalizados';
import FinanceiroDespesas from './pages/financeiro/FinanceiroDespesas';
import FinanceiroRelatorio from './pages/financeiro/FinanceiroRelatorio';
import FinanceiroConfiguracoes from './pages/financeiro/FinanceiroConfiguracoes';

// WhatsApp Module (Add-on)
import WhatsAppHome from './pages/whatsapp/WhatsAppHome';
import Atendimento from './pages/whatsapp/Atendimento';
import ContatosWA from './pages/whatsapp/ContatosWA';
import TemplatesWA from './pages/whatsapp/TemplatesWA';
import ChatbotWA from './pages/whatsapp/ChatbotWA';
import AnalyticsWA from './pages/whatsapp/AnalyticsWA';
import ConexaoWhatsApp from './pages/whatsapp/ConexaoWhatsApp';
import LogsWA from './pages/whatsapp/LogsWA';

// Components
import Layout from './components/Layout';
import { cargosAPI } from './lib/api';

// Protected Route Component with Permission Check
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = window.location.pathname;
  const [permissions, setPermissions] = React.useState(null);
  const [checkingPermissions, setCheckingPermissions] = React.useState(true);

  // Rotas públicas - acessíveis a qualquer usuário logado sem verificação de permissão
  const publicRoutes = ['/suporte', '/base-conhecimento'];

  React.useEffect(() => {
    const checkPermissions = async () => {
      if (!user) {
        setCheckingPermissions(false);
        return;
      }
      
      try {
        const response = await cargosAPI.getMinhasPermissoes();
        setPermissions(response.data.paginas || []);
      } catch (error) {
        console.error('Erro ao verificar permissões:', error);
        setPermissions(['/']); // Fallback
      } finally {
        setCheckingPermissions(false);
      }
    };
    
    checkPermissions();
  }, [user]);

  if (loading || checkingPermissions) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Rotas ocultas - acessíveis apenas por URL direta (para admins master)
  const hiddenRoutes = ['/admin/tenants', '/admin/logs'];
  const isHiddenRoute = hiddenRoutes.includes(location);
  
  // Se for rota oculta, verificar se é master ou admin
  if (isHiddenRoute) {
    const isMasterOrAdmin = user.role === 'master' || user.role === 'admin' || user.cargo === 'master' || user.cargo === 'admin';
    if (isMasterOrAdmin) {
      return <Layout>{children}</Layout>;
    }
    // Não é master/admin, redirecionar
    return <Navigate to="/" replace />;
  }

  // Verificar se é rota pública (acessível a todos os usuários logados)
  const isPublicRoute = publicRoutes.some(route => location.startsWith(route));
  if (isPublicRoute) {
    return <Layout>{children}</Layout>;
  }

  // Verificar se tem permissão para a página atual
  if (permissions && permissions.length > 0) {
    const hasPermission = permissions.some(p => {
      if (p === '/') return location === '/';
      // páginas do atendimento são liberadas uma a uma no cargo: "/whatsapp" não libera "/whatsapp/contatos"
      if (p === '/whatsapp') return location === '/whatsapp' || location === '/whatsapp/';
      return location.startsWith(p);
    });
    
    if (!hasPermission) {
      // Redirecionar para a primeira página permitida
      return <Navigate to={permissions[0]} replace />;
    }
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const [redirectTo, setRedirectTo] = React.useState(null);

  React.useEffect(() => {
    const getRedirectPath = async () => {
      if (user) {
        try {
          const response = await cargosAPI.getMinhasPermissoes();
          const paginas = response.data.paginas || [];
          setRedirectTo(paginas.length > 0 ? paginas[0] : '/');
        } catch (error) {
          setRedirectTo('/');
        }
      }
    };
    
    getRedirectPath();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user && redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  if (user && !redirectTo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projetos"
        element={
          <ProtectedRoute>
            <Projetos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientes"
        element={
          <ProtectedRoute>
            <ClientList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientes/novo"
        element={
          <ProtectedRoute>
            <ClientForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientes/:id"
        element={
          <ProtectedRoute>
            <ClientForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/propostas"
        element={
          <ProtectedRoute>
            <Propostas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/atividades"
        element={
          <ProtectedRoute>
            <Atividades />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projetos/novo"
        element={
          <ProtectedRoute>
            <NewProject />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projetos/suspensos"
        element={
          <ProtectedRoute>
            <Projetos key="suspensos" modo="suspensos" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projetos/arquivados"
        element={
          <ProtectedRoute>
            <ArchivedProjects />
          </ProtectedRoute>
        }
      />
      <Route
        path="/relatorios"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin/parceiros"
        element={
          <ProtectedRoute adminOnly>
            <Partners />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/usuarios"
        element={
          <ProtectedRoute adminOnly>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/etapas"
        element={
          <ProtectedRoute adminOnly>
            <Etapas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/categorias"
        element={
          <ProtectedRoute adminOnly>
            <Categorias />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/marcas"
        element={
          <ProtectedRoute adminOnly>
            <Marcas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/config"
        element={
          <ProtectedRoute adminOnly>
            <ConfigPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/configuracoes"
        element={
          <ProtectedRoute adminOnly>
            <Configuracoes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/empresa"
        element={
          <ProtectedRoute adminOnly>
            <ConfiguracaoEmpresa />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cargos"
        element={
          <ProtectedRoute adminOnly>
            <Cargos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/status-projeto"
        element={
          <ProtectedRoute adminOnly>
            <StatusProjeto />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tipos-projeto"
        element={
          <ProtectedRoute adminOnly>
            <TiposProjeto />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eliminar-dados"
        element={
          <ProtectedRoute adminOnly>
            <EliminarDados />
          </ProtectedRoute>
        }
      />

      {/* Suporte Routes */}
      <Route
        path="/suporte"
        element={
          <ProtectedRoute>
            <Suporte />
          </ProtectedRoute>
        }
      />
      <Route
        path="/base-conhecimento"
        element={
          <ProtectedRoute>
            <BaseConhecimento />
          </ProtectedRoute>
        }
      />
      <Route
        path="/base-conhecimento/:artigoId"
        element={
          <ProtectedRoute>
            <BaseConhecimento />
          </ProtectedRoute>
        }
      />

      {/* Payment Routes (Public - sem autenticação) */}
      <Route path="/pagamento/sucesso" element={<PaymentSuccess />} />
      <Route path="/pagamento/cancelado" element={<PaymentCanceled />} />
      <Route path="/acesso-bloqueado" element={<AcessoBloqueado />} />
      <Route path="/manutencao" element={<Manutencao />} />

      {/* Hidden Admin Route - Tenants Management */}
      <Route
        path="/admin/tenants"
        element={
          <ProtectedRoute adminOnly>
            <TenantsAdmin />
          </ProtectedRoute>
        }
      />

      {/* Hidden Admin Route - Audit Logs */}
      <Route
        path="/admin/logs"
        element={
          <ProtectedRoute adminOnly>
            <Logs />
          </ProtectedRoute>
        }
      />

      {/* ==================== FINANCEIRO MODULE (Add-on) ==================== */}
      <Route path="/financeiro" element={<ProtectedRoute><FinanceiroDashboard /></ProtectedRoute>} />
      <Route path="/financeiro/servicos" element={<ProtectedRoute><FinanceiroServicos /></ProtectedRoute>} />
      <Route path="/financeiro/finalizados" element={<ProtectedRoute><FinanceiroFinalizados /></ProtectedRoute>} />
      <Route path="/financeiro/despesas" element={<ProtectedRoute><FinanceiroDespesas /></ProtectedRoute>} />
      <Route path="/financeiro/relatorio" element={<ProtectedRoute><FinanceiroRelatorio /></ProtectedRoute>} />
      <Route path="/financeiro/clientes" element={<ProtectedRoute><ClientList /></ProtectedRoute>} />
      <Route path="/financeiro/configuracoes" element={<ProtectedRoute><FinanceiroConfiguracoes /></ProtectedRoute>} />

      {/* ==================== WHATSAPP MODULE (Add-on) ==================== */}
      <Route path="/whatsapp" element={<ProtectedRoute><WhatsAppHome><Atendimento /></WhatsAppHome></ProtectedRoute>} />
      <Route path="/whatsapp/contatos" element={<ProtectedRoute><WhatsAppHome><ContatosWA /></WhatsAppHome></ProtectedRoute>} />
      <Route path="/whatsapp/templates" element={<ProtectedRoute><WhatsAppHome><TemplatesWA /></WhatsAppHome></ProtectedRoute>} />
      <Route path="/whatsapp/chatbot" element={<ProtectedRoute><WhatsAppHome><ChatbotWA /></WhatsAppHome></ProtectedRoute>} />
      <Route path="/whatsapp/analytics" element={<ProtectedRoute><WhatsAppHome><AnalyticsWA /></WhatsAppHome></ProtectedRoute>} />
      <Route path="/whatsapp/conexao" element={<ProtectedRoute><WhatsAppHome><ConexaoWhatsApp /></WhatsAppHome></ProtectedRoute>} />
      <Route path="/whatsapp/logs" element={<ProtectedRoute><WhatsAppHome><LogsWA /></WhatsAppHome></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
